import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  ButtonProps,
  useDisclosure,
  useToken,
} from "@chakra-ui/react";
import { FC, MouseEvent, useCallback, useRef, useState } from "react";

import { AddIcon } from "@nens/ranalib";

import { useNeedsCreatorRoleTitle } from "../queries/general";

import TimerButton from "./TimerButton";

interface ButtonWithConfirmationProps extends ButtonProps {
  buttonText: string;
  dialogTitle?: string;
  confirmationButtonText: string;
  confirmationButtonSafety?: "safe" | "neutral" | "dangerous"; // Decides colorscheme
  customTitle?: string;
  disableAfterClick?: boolean;
  height?: string;
  timer?: boolean;
  needsCreatorRole?: boolean;
  buttonIcon?: React.ReactElement;
}

const ButtonWithConfirmation: FC<ButtonWithConfirmationProps> = ({
  onClick,
  buttonText,
  dialogTitle = "Please Confirm",
  customTitle,
  confirmationButtonText,
  confirmationButtonSafety = "dangerous",
  disableAfterClick = false,
  disabled,
  children,
  height,
  timer,
  needsCreatorRole,
  buttonIcon,
  ...rest
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isClicked, setIsClicked] = useState<boolean>(false);

  const cancelRef = useRef<HTMLButtonElement>(null);

  const needsCreatorTitle = useNeedsCreatorRoleTitle();
  const isCreatorRoleNeededDisabled = needsCreatorRole && !!needsCreatorTitle;

  const onConfirm = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setIsClicked(true);
      onClose();
      if (onClick) onClick(event);
    },
    [onClose, onClick]
  );

  // Styling for the main trigger button (uses custom variants)
  const triggerButtonVariant = {
    dangerous: "danger",
    safe: "primary",
    neutral: "secondary",
  }[confirmationButtonSafety];

  // Styling for the confirmation button inside the dialog (uses Chakra colorSchemes)
  const confirmationButtonColorScheme = (
    {
      dangerous: "red",
      safe: "green",
      neutral: "blue",
    } as const
  )[confirmationButtonSafety];

  // Get the default button height from Chakra theme
  const [defaultButtonHeight] = useToken("sizes", ["button"]);

  return (
    <>
      <Button
        onClick={onOpen}
        {...rest}
        isDisabled={disabled || (isClicked && disableAfterClick) || isCreatorRoleNeededDisabled}
        title={customTitle || needsCreatorTitle}
        leftIcon={buttonIcon || undefined}
        variant={triggerButtonVariant}
        height={height ?? defaultButtonHeight}
      >
        {buttonText}
      </Button>
      <AlertDialog
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isOpen={isOpen}
        onEsc={onClose}
        onOverlayClick={onClose}
        isCentered
      >
        <AlertDialogOverlay />

        <AlertDialogContent>
          <AlertDialogHeader>{dialogTitle}</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>{children}</AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>
              Cancel
            </Button>
            {!timer && (
              <Button colorScheme={confirmationButtonColorScheme} ml={3} onClick={onConfirm}>
                {confirmationButtonText}
              </Button>
            )}
            {timer && (
              <TimerButton colorScheme={confirmationButtonColorScheme} onConfirm={onConfirm}>
                {confirmationButtonText}
              </TimerButton>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ButtonWithConfirmation;
